import { useState, useEffect, useRef, useCallback } from "react";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import { Send, MessageSquare, X, Minimize2, RefreshCw, Wifi, WifiOff } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { cs } from "date-fns/locale";

interface ChatMessage {
  id: string;
  user_id: string;
  message: string;
  created_at: string;
  profiles?: {
    username: string | null;
  } | null;
  isOptimistic?: boolean;
  isSending?: boolean;
}

export const UserFeedbackChat = () => {
  const { user, isAuthenticated } = useAuth();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [newMessage, setNewMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [isConnected, setIsConnected] = useState(true);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const channelRef = useRef<any>(null);
  const fallbackIntervalRef = useRef<number>();

  // Enhanced fetch messages with optimistic update filtering
  const fetchMessages = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('chat_messages')
        .select(`
          id,
          user_id,
          message,
          created_at,
          profiles (username)
        `)
        .order('created_at', { ascending: true })
        .limit(50);

      if (error) throw error;
      
      setMessages(prev => {
        // Merge with optimistic messages that are still being sent
        const optimisticMessages = prev.filter(msg => msg.isOptimistic && msg.isSending);
        const serverMessages = data || [];
        
        // Remove optimistic messages that have been confirmed on server
        const filteredOptimistic = optimisticMessages.filter(opt => 
          !serverMessages.some(server => 
            server.message === opt.message && 
            server.user_id === opt.user_id &&
            Math.abs(new Date(server.created_at).getTime() - new Date(opt.created_at).getTime()) < 10000
          )
        );
        
        return [...serverMessages, ...filteredOptimistic];
      });
      
      setTimeout(scrollToBottom, 100);
    } catch (error) {
      console.error('Error fetching messages:', error);
      setIsConnected(false);
    }
  }, []);

  // Fallback polling mechanism
  const startFallbackPolling = useCallback(() => {
    console.log('Starting fallback polling...');
    fallbackIntervalRef.current = window.setInterval(() => {
      if (isOpen) {
        console.log('Polling for new messages...');
        fetchMessages();
        setLastRefresh(new Date());
      }
    }, 5000);
  }, [isOpen, fetchMessages]);

  // Enhanced real-time subscription with error handling and fallback
  const setupRealtimeSubscription = useCallback(() => {
    if (!isAuthenticated) return;

    console.log('Setting up realtime subscription...');
    
    // Clean up existing subscription
    if (channelRef.current) {
      supabase.removeChannel(channelRef.current);
    }

    // Clear fallback interval
    if (fallbackIntervalRef.current) {
      clearInterval(fallbackIntervalRef.current);
    }

    const channel = supabase
      .channel('chat_messages_enhanced', {
        config: {
          broadcast: { self: false },
          presence: { key: user?.id }
        }
      })
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'chat_messages'
        },
        (payload) => {
          console.log('Real-time message received:', payload);
          setIsConnected(true);
          setLastRefresh(new Date());
          
          // Remove any optimistic messages for this user
          if (user && payload.new) {
            setMessages(prev => prev.filter(msg => !msg.isOptimistic || msg.user_id !== user.id));
          }
          
          // Refresh all messages to get proper username data
          fetchMessages();
        }
      )
      .on('broadcast', { event: 'test' }, () => {
        console.log('Broadcast test received');
        setIsConnected(true);
      })
      .subscribe((status, err) => {
        console.log('Subscription status:', status, err);
        
        if (status === 'SUBSCRIBED') {
          setIsConnected(true);
          console.log('Successfully subscribed to real-time updates');
        } else if (status === 'CHANNEL_ERROR') {
          console.error('Real-time subscription error:', err);
          setIsConnected(false);
          // Start fallback polling
          startFallbackPolling();
        } else if (status === 'TIMED_OUT') {
          console.warn('Real-time subscription timed out, retrying...');
          setIsConnected(false);
          // Retry subscription after a delay
          setTimeout(() => setupRealtimeSubscription(), 3000);
        }
      });

    channelRef.current = channel;

    // Test the connection
    setTimeout(() => {
      channel.send({
        type: 'broadcast',
        event: 'test',
        payload: { test: true }
      });
    }, 1000);

    return channel;
  }, [isAuthenticated, user?.id, fetchMessages, startFallbackPolling]);

  // Setup subscription and page visibility handling
  useEffect(() => {
    if (isAuthenticated) {
      fetchMessages();
      setupRealtimeSubscription();

      // Handle page visibility changes (important for mobile)
      const handleVisibilityChange = () => {
        if (!document.hidden && isOpen) {
          console.log('Page became visible, refreshing connection...');
          setupRealtimeSubscription();
          fetchMessages();
        }
      };

      // Handle online/offline events
      const handleOnline = () => {
        console.log('Back online, reconnecting...');
        setIsConnected(true);
        setupRealtimeSubscription();
        fetchMessages();
      };

      const handleOffline = () => {
        console.log('Gone offline');
        setIsConnected(false);
        startFallbackPolling();
      };

      document.addEventListener('visibilitychange', handleVisibilityChange);
      window.addEventListener('online', handleOnline);
      window.addEventListener('offline', handleOffline);

      return () => {
        if (channelRef.current) {
          supabase.removeChannel(channelRef.current);
        }
        if (fallbackIntervalRef.current) {
          clearInterval(fallbackIntervalRef.current);
        }
        document.removeEventListener('visibilitychange', handleVisibilityChange);
        window.removeEventListener('online', handleOnline);
        window.removeEventListener('offline', handleOffline);
      };
    }
  }, [isAuthenticated, setupRealtimeSubscription, fetchMessages, startFallbackPolling, isOpen]);

  // Refresh when chat window opens
  useEffect(() => {
    if (isOpen && isAuthenticated) {
      fetchMessages();
      setupRealtimeSubscription();
    }
  }, [isOpen, isAuthenticated, fetchMessages, setupRealtimeSubscription]);

  const scrollToBottom = () => {
    if (scrollAreaRef.current) {
      const scrollContainer = scrollAreaRef.current.querySelector('[data-radix-scroll-area-viewport]');
      if (scrollContainer) {
        scrollContainer.scrollTop = scrollContainer.scrollHeight;
      }
    }
  };

  // Enhanced sendMessage with optimistic updates
  const sendMessage = async () => {
    if (!newMessage.trim() || !user || newMessage.length > 160) return;

    const optimisticMessage: ChatMessage = {
      id: `optimistic-${Date.now()}`,
      user_id: user.id,
      message: newMessage.trim(),
      created_at: new Date().toISOString(),
      profiles: { username: null },
      isOptimistic: true,
      isSending: true
    };

    // Add optimistic message immediately
    setMessages(prev => [...prev, optimisticMessage]);
    const messageToSend = newMessage.trim();
    setNewMessage("");
    scrollToBottom();

    setIsLoading(true);
    try {
      const { error } = await supabase
        .from('chat_messages')
        .insert({
          user_id: user.id,
          message: messageToSend
        });

      if (error) throw error;

      // Remove the optimistic message - real-time will add the confirmed one
      setMessages(prev => prev.filter(msg => msg.id !== optimisticMessage.id));
      
      toast.success("Zpráva odeslána");
      
      // Fallback refresh po odeslání zprávy aby se zajistilo zobrazení
      setTimeout(() => {
        fetchMessages();
        // Ujistit se že je real-time aktivní
        if (!isConnected) {
          setupRealtimeSubscription();
        }
      }, 1000);
      
    } catch (error) {
      console.error('Error sending message:', error);
      // Remove failed optimistic message
      setMessages(prev => prev.filter(msg => msg.id !== optimisticMessage.id));
      // Restore the message text
      setNewMessage(messageToSend);
      toast.error("Chyba při odesílání zprávy");
    } finally {
      setIsLoading(false);
    }
  };

  // Manual refresh function
  const refreshMessages = useCallback(() => {
    fetchMessages();
    setupRealtimeSubscription();
    setLastRefresh(new Date());
  }, [fetchMessages, setupRealtimeSubscription]);

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  if (!isAuthenticated) {
    return null; // Don't show anything for unauthenticated users
  }

  const handleOpenChat = () => {
    setIsOpen(true);
    // Automaticky aktivovat online mód při otevření
    if (isAuthenticated) {
      setupRealtimeSubscription();
      fetchMessages();
    }
  };

  return (
    <div className="fixed bottom-4 right-4 z-[60]">
      {/* Floating trigger button */}
      {!isOpen && (
        <Button
          onClick={handleOpenChat}
          className="h-12 w-12 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 bg-primary hover:bg-primary/90"
          size="icon"
        >
          <MessageSquare className="h-6 w-6" />
        </Button>
      )}

      {/* Chat window */}
      {isOpen && (
        <Card className={`w-80 max-w-[calc(100vw-2rem)] shadow-xl border transition-all duration-300 ${
          isMinimized ? 'h-12' : 'h-96'
        }`}>
          <CardHeader className="pb-2 px-4 py-3 border-b">
            <CardTitle className="text-sm flex items-center gap-2">
              <MessageSquare className="h-4 w-4" />
              Náměty a zpětná vazba
              
              {/* Connection status indicator */}
              <div className="flex items-center gap-1">
                {isConnected ? (
                  <Wifi className="h-3 w-3 text-green-500" />
                ) : (
                  <WifiOff className="h-3 w-3 text-red-500" />
                )}
              </div>
              
              <div className="ml-auto flex items-center gap-1">
                {/* Refresh button */}
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6"
                  onClick={refreshMessages}
                  title="Obnovit zprávy"
                >
                  <RefreshCw className="h-3 w-3" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6"
                  onClick={() => setIsMinimized(!isMinimized)}
                >
                  <Minimize2 className="h-3 w-3" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6"
                  onClick={() => setIsOpen(false)}
                >
                  <X className="h-3 w-3" />
                </Button>
              </div>
            </CardTitle>
          </CardHeader>
          
          {!isMinimized && (
            <CardContent className="p-4 flex flex-col h-[calc(100%-3rem)]">
              <ScrollArea 
                ref={scrollAreaRef}
                className="flex-1 w-full border rounded-md p-3 mb-3"
              >
                <div className="space-y-3">
                  {messages.length === 0 ? (
                    <p className="text-xs text-muted-foreground text-center py-4">
                      Zatím žádné zprávy. Buďte první!
                    </p>
                  ) : (
                    messages.map((msg) => (
                      <div key={msg.id} className="space-y-1">
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <span className="font-medium">
                            {msg.profiles?.username || msg.user_id.slice(0, 8)}
                          </span>
                          <span>•</span>
                          <span>
                            {formatDistanceToNow(new Date(msg.created_at), {
                              addSuffix: true,
                              locale: cs
                            })}
                          </span>
                        </div>
                         <p className={`text-xs rounded-md p-2 ${
                           msg.isOptimistic 
                             ? 'bg-muted/30 opacity-70 border border-dashed border-muted-foreground/30' 
                             : 'bg-muted/50'
                         }`}>
                           {msg.message}
                           {msg.isSending && (
                             <span className="ml-2 inline-block animate-pulse text-muted-foreground">
                               ...
                             </span>
                           )}
                         </p>
                      </div>
                    ))
                  )}
                </div>
              </ScrollArea>

              <div className="space-y-2">
                <Textarea
                  placeholder="Vaše náměty, připomínky nebo zpětná vazba... (max 160 znaků)"
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  maxLength={160}
                  className="min-h-[50px] resize-none text-xs"
                />
                
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">
                    {newMessage.length}/160
                  </span>
                  
                  <Button
                    onClick={sendMessage}
                    disabled={!newMessage.trim() || isLoading || newMessage.length > 160}
                    size="sm"
                    className="gap-1 h-7 px-2"
                  >
                    <Send className="h-3 w-3" />
                    Odeslat
                  </Button>
                </div>
              </div>

              <p className="text-xs text-muted-foreground mt-2">
                💡 Sdílejte své nápady na vylepšení aplikace
              </p>
            </CardContent>
          )}
        </Card>
      )}
    </div>
  );
};